import { z } from 'zod/v4';

/**
 * Preprocessor that coerces a value to a string (unless it's null or undefined),
 * and then converts empty strings or original null/undefined to undefined.
 */
const emptyStringToUndefined = (val: string | number | null | undefined) => {
  if (val === null || val === undefined) {
    return undefined;
  }
  const strVal = String(val);
  if (strVal.trim() === '') {
    return undefined;
  }
  return val;
};

/**
 * A Zod schema for optional strings and numbers
 * and transforms empty strings to undefined before validation.
 */
export const zCoercedOptional = <T>(schema: z.ZodType<T>) =>
  z.preprocess(emptyStringToUndefined, schema) as unknown as z.ZodType<T, T>;

/**
 * Preprocessor that filters out empty strings from string arrays.
 */
const filterValidStringArrayPreprocessor = (val: unknown) => {
  if (!Array.isArray(val)) return val;
  return val.filter((item) => typeof item === 'string' && item.trim() !== '');
};

/**
 * A Zod schema that filters out empty strings from string arrays before validation.
 * This function is specifically designed for array schemas and ensures type safety
 * by constraining both input and output to array types.
 *
 * @param schema - A Zod array schema (e.g., z.array(z.string()))
 * @returns A Zod schema that filters empty strings before validation
 */
export const zFilteredStringArray = <T>(schema: z.ZodArray<z.ZodType<T>>) =>
  z.preprocess(filterValidStringArrayPreprocessor, schema) as z.ZodType<T[], T[]>;

/**
 * Preprocessor that filters out objects where all properties are empty/falsy values.
 */
type FilterEmptyObjectsFromArrayOptions = {
  /**
   * Whether to filter out objects with all empty/falsy values from object arrays before validation.
   */
  ignoreKeys?: string[];
};

/**
 * Preprocessor that ensures a value is always an array.
 * Converts non-array values to empty arrays.
 */
const ensureArray = (val: unknown) => (Array.isArray(val) ? val : []);

/**
 * A Zod schema for required arrays that defaults to empty array.
 * Useful for enum arrays or other required array fields.
 */
export const zRequiredArray = <T>(schema: z.ZodArray<z.ZodType<T>>) =>
  z.preprocess(ensureArray, schema) as z.ZodType<T[], T[]>;

/**
 * Preprocessor that handles decimal number input, allowing intermediate states
 * like "123." while typing, but converting to proper numbers for validation.
 * Returns null for empty values to signal deletion to the backend.
 */
const decimalNumberPreprocessor = (val: string | number | null | undefined) => {
  if (val === null || val === undefined) {
    return null;
  }

  if (typeof val === 'number') {
    return val;
  }

  const stringVal = String(val).trim();

  if (stringVal === '') {
    return null;
  }

  // Allow intermediate states ending with decimal point for better UX
  let cleanedValue = stringVal;
  if (stringVal.endsWith('.')) {
    cleanedValue = stringVal.slice(0, -1);
  }

  if (cleanedValue === '') {
    return null;
  }

  const parsed = parseFloat(cleanedValue);

  if (isNaN(parsed)) {
    throw new Error('Must be a valid number');
  }

  if (parsed < 0) {
    throw new Error('Must be a non-negative number');
  }

  return parsed;
};

/**
 * A Zod schema for optional decimal numbers that handles string input
 * and allows intermediate typing states like "123." while providing
 * proper validation and transformation to numbers. Returns null for empty
 * values to signal deletion to the backend.
 */
export const zDecimalNumber = z.preprocess(decimalNumberPreprocessor, z.number().optional().nullable()) as z.ZodType<
  number | undefined
>;

/**
 * Creates a Zod array schema that filters out empty objects before validation.
 * This ensures validation error indices match the filtered array.
 */
export const zArrayWithoutEmptyObjectSchema = <T extends z.ZodTypeAny>(
  itemSchema: T,
  options?: FilterEmptyObjectsFromArrayOptions,
) => {
  /**
   * Helper that determines whether an object has any meaningful (non-empty) value
   * after optionally excluding some keys.
   */
  const isNonEmptyObject = (obj: unknown): obj is Record<string, unknown> => {
    if (typeof obj !== 'object' || obj === null) return false;

    const allKeys = Object.keys(obj);
    const keysToCheck = options?.ignoreKeys ? allKeys.filter((key) => !options.ignoreKeys!.includes(key)) : allKeys;

    return keysToCheck.some((key) => {
      const value = (obj as Record<string, unknown>)[key];
      return value !== undefined && value !== null && value !== '';
    });
  };

  return z.array(z.any()).transform((arr: unknown[]) => {
    const filteredItems: z.output<T>[] = [];
    const errors: z.core.$ZodIssue[] = [];

    arr.forEach((item, index) => {
      // Skip completely empty objects.
      if (!isNonEmptyObject(item)) return;

      // Validate non-empty objects against the provided item schema.
      const result = itemSchema.safeParse(item);

      if (result.success) {
        filteredItems.push(result.data);
      } else {
        // Re-map each issue so that the error path starts with the original index.
        result.error.issues.forEach((issue) => {
          errors.push({
            ...issue,
            path: [index, ...issue.path],
          });
        });
      }
    });

    // If there were validation errors, throw them
    if (errors.length > 0) {
      throw new z.ZodError(errors);
    }

    return filteredItems;
  });
};
